import {
  useDraggingDrawer,
  useSplitViewDragging,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useEffect, useRef } from 'react';
import { useIsPdfViewerDragging } from './Draggable/context';
import styles from './Viewer.module.scss';
const cx = classnames.bind(styles);
type UploadFileEvent = CustomEvent<{ file: File }>;

const enumPreviewMode = {
  default: 'default',
  no_file: 'no_file',
  drop_and_drag: 'drop_and_drag',
} as const;

type PreviewMode = keyof typeof enumPreviewMode;

export type Props = {
  src?: string;
  className?: string;
  onChangeFile?: (file: File) => void;
  isOverlay?: boolean;
  mode?: PreviewMode;
};

export const PdfViewer: FC<Props> = memo(
  ({ src, className, onChangeFile, isOverlay, mode = 'default' }) => {
    const isDrawerDragging = useDraggingDrawer();
    const isSplitViewDragging = useSplitViewDragging();
    const isPdfViewerDragging = useIsPdfViewerDragging();
    const ref = useRef<HTMLElement>(null);
    useEffect(() => {
      if (!ref.current) return;
      const ev = (e: UploadFileEvent) => {
        onChangeFile?.(e.detail.file);
      };
      ref.current?.addEventListener('wc.pdf.viewer.fileinputchange', ev);
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        ref?.current?.removeEventListener('wc.pdf.viewer.fileinputchange', ev);
      };
    }, [onChangeFile]);
    return (
      <div className={cx(styles['container'], className)}>
        <pdf-viewer
          // eslint-disable-next-line @moneyforward/account-payable/literals-in-props
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          }}
          src={src}
          default-pdf-url={src}
          baseUrl={`${import.meta.env.BASE_URL}pdf/web/`}
          ref={ref}
          preview-mode={mode}
        />
        <div
          // eslint-disable-next-line @moneyforward/account-payable/literals-in-props
          style={{
            display:
              isDrawerDragging ||
              isSplitViewDragging ||
              isPdfViewerDragging ||
              isOverlay
                ? 'block'
                : 'none',
          }}
          className={cx(styles['overlay'])}
        />
      </div>
    );
  }
);
PdfViewer.displayName = 'PdfViewer';
